import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Users, RefreshCw, Filter, Clock, Info, Pencil, Check, X, Plus, Trash2,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Skeleton } from '../components/ui/Skeleton.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { SegmentBuilder } from '../components/segments/SegmentBuilder.tsx';
import { AddSegmentMemberModal } from '../components/segments/AddSegmentMemberModal.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Segment {
  id: string;
  name: string;
  description: string | null;
  type: 'dynamic' | 'static';
  status: string;
  definitionJson: Record<string, unknown>;
  memberCount: number | null;
  lastComputedAt: string | null;
  createdAt: string;
  updatedAt: string;
  isComputing?: boolean;
}

interface SegmentMember {
  id: string;
  firstName: string | null;
  lastName: string | null;
  status: string;
  createdAt: string;
  enteredAt: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-zinc-50 last:border-0">
      <span className="text-xs text-zinc-500 shrink-0">{label}</span>
      <span className="text-xs text-zinc-800 font-medium text-right break-all">{value ?? '—'}</span>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SegmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [recomputing, setRecomputing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDsl, setEditingDsl] = useState<Record<string, unknown>>({});
  const [showAddMember, setShowAddMember] = useState(false);

  // ── queries ─────────────────────────────────────────────────────────────────

  const { data: segment, isLoading } = useQuery<Segment>({
    queryKey: ['segment', id],
    queryFn: () => api.get(`/api/v1/segments/${id}`).then((r) => r.data),
    enabled: !!id,
    refetchInterval: (query) => (query.state.data?.isComputing ? 3000 : false),
  });

  const { data: membersData, isLoading: membersLoading } = useQuery<{
    items: SegmentMember[];
    total: number;
  }>({
    queryKey: ['segment-members', id],
    queryFn: () => api.get(`/api/v1/segments/${id}/members`).then((r) => r.data),
    enabled: !!id,
  });
  const members = membersData?.items ?? [];

  const existingMemberIds = useMemo(
    () => new Set(members.map((m) => m.id)),
    [members],
  );

  // ── mutations ────────────────────────────────────────────────────────────────

  const recomputeMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/segments/${id}/recompute`),
    onMutate: () => setRecomputing(true),
    onSuccess: () => {
      toast.success('Recomputation started — members will refresh shortly');
      qc.invalidateQueries({ queryKey: ['segment', id] });
      setRecomputing(false);
    },
    onError: (err) => {
      toast.error(extractError(err));
      setRecomputing(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (definition: Record<string, unknown>) =>
      api.patch(`/api/v1/segments/${id}`, { definition }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['segment', id] });
      setIsEditing(false);
      toast.success('Segment definition updated');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to update definition')),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (customerId: string) =>
      api.delete(`/api/v1/segments/${id}/members/${customerId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['segment-members', id] });
      qc.invalidateQueries({ queryKey: ['segment', id] });
      toast.success('Member removed');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to remove member')),
  });

  // ── handlers ─────────────────────────────────────────────────────────────────

  const startEditing = () => {
    setEditingDsl(segment?.definitionJson ?? {});
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditingDsl({});
  };

  const saveDefinition = () => {
    updateMutation.mutate(editingDsl);
  };

  // ── invalidate members when computing finishes ────────────────────────────────

  const wasComputing = useRef(false);
  useEffect(() => {
    const computing = !!(recomputing || segment?.isComputing);
    if (wasComputing.current && !computing) {
      qc.invalidateQueries({ queryKey: ['segment-members', id] });
    }
    wasComputing.current = computing;
  }, [recomputing, segment?.isComputing, id, qc]);

  // ── loading state ─────────────────────────────────────────────────────────────

  if (isLoading || !segment) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const typeVariant = segment.type === 'dynamic' ? 'info' : 'default';
  const isStatic = segment.type === 'static';

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/segments')}
          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-zinc-900 truncate">{segment.name}</h1>
          {segment.description && (
            <p className="text-xs text-zinc-400 mt-0.5">{segment.description}</p>
          )}
        </div>
        <Badge variant={typeVariant}>{segment.type}</Badge>
        <Badge variant={segment.status === 'active' ? 'success' : 'default'}>
          {segment.status}
        </Badge>
        {segment.type === 'dynamic' && (
          <Button
            size="sm"
            variant="secondary"
            loading={recomputing || recomputeMutation.isPending || segment.isComputing}
            onClick={() => recomputeMutation.mutate()}
          >
            <RefreshCw size={12} className="mr-1" /> Recompute
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left — info */}
        <div className="space-y-5 lg:col-span-1">
          {/* Segment details */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-zinc-100">
              <Info size={14} className="text-zinc-400" />
              <h2 className="text-sm font-semibold text-zinc-700">Details</h2>
            </div>
            <div className="p-5">
              <KV label="ID" value={<code className="font-mono text-[10px]">{segment.id}</code>} />
              <KV label="Type" value={<Badge variant={typeVariant}>{segment.type}</Badge>} />
              <KV
                label="Status"
                value={
                  <Badge variant={segment.status === 'active' ? 'success' : 'default'}>
                    {segment.status}
                  </Badge>
                }
              />
              <KV
                label="Members"
                value={
                  <span className="font-semibold tabular-nums">
                    {segment.memberCount != null
                      ? segment.memberCount.toLocaleString()
                      : (membersData?.total ?? members.length).toLocaleString()}
                  </span>
                }
              />
              <KV label="Last computed" value={fmt(segment.lastComputedAt)} />
              <KV label="Created" value={fmt(segment.createdAt)} />
              <KV label="Updated" value={fmt(segment.updatedAt)} />
            </div>
          </div>

          {/* Definition */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-100">
              <div className="flex items-center gap-2">
                <Filter size={14} className="text-zinc-400" />
                <h2 className="text-sm font-semibold text-zinc-700">Definition</h2>
              </div>
              {/* Edit / Save / Cancel — only for dynamic segments */}
              {segment.type === 'dynamic' && !isEditing && (
                <button
                  onClick={startEditing}
                  className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-700 transition-colors"
                >
                  <Pencil size={12} /> Edit
                </button>
              )}
              {isEditing && (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={cancelEditing}
                    className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
                  >
                    <X size={12} /> Cancel
                  </button>
                  <button
                    onClick={saveDefinition}
                    disabled={updateMutation.isPending}
                    className="flex items-center gap-1 text-xs text-zinc-900 font-medium hover:text-zinc-600 disabled:opacity-50 transition-colors"
                  >
                    <Check size={12} /> {updateMutation.isPending ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>
            <div className="p-5">
              {isEditing ? (
                <SegmentBuilder value={editingDsl} onChange={setEditingDsl} />
              ) : (
                <pre className="text-[11px] text-zinc-700 font-mono bg-zinc-50 rounded-lg p-3 overflow-auto max-h-64 whitespace-pre-wrap break-all">
                  {JSON.stringify(segment.definitionJson, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>

        {/* Right — members */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-100">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-zinc-400" />
                <h2 className="text-sm font-semibold text-zinc-700">
                  Members · {(membersData?.total ?? members.length).toLocaleString()}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                {(recomputing || segment.isComputing) && (
                  <span className="flex items-center gap-1.5 text-[11px] text-amber-500 animate-pulse">
                    <RefreshCw size={11} className="animate-spin" /> Computing…
                  </span>
                )}
                {isStatic && (
                  <Button size="sm" variant="secondary" onClick={() => setShowAddMember(true)}>
                    <Plus size={12} className="mr-1" /> Add members
                  </Button>
                )}
              </div>
            </div>

            {membersLoading ? (
              <div className="p-5 space-y-2">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : members.length === 0 ? (
              <div className="p-10 text-center">
                <Users size={24} className="text-zinc-300 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">No members in this segment</p>
                {segment.type === 'dynamic' && (
                  <p className="text-xs text-zinc-400 mt-1">
                    Try running a recomputation to populate members.
                  </p>
                )}
                {isStatic && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mt-3"
                    onClick={() => setShowAddMember(true)}
                  >
                    <Plus size={12} className="mr-1" /> Add members
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-zinc-100">
                      <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                        Customer
                      </th>
                      <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">Name</th>
                      <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">Status</th>
                      <th className="px-5 pb-2 pt-3 text-left text-zinc-400 font-medium">
                        Entered
                      </th>
                      {isStatic && <th className="px-5 pb-2 pt-3 w-12" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-50">
                    {members.map((m) => (
                      <tr key={m.id} className="hover:bg-zinc-50 transition-colors">
                        <td className="px-5 py-2.5">
                          <button
                            onClick={() => navigate(`/customers/${m.id}`)}
                            className="font-mono text-[10px] text-zinc-500 hover:text-zinc-900 hover:underline"
                          >
                            {m.id.slice(0, 8)}…
                          </button>
                        </td>
                        <td className="px-5 py-2.5 text-zinc-700">
                          {[m.firstName, m.lastName].filter(Boolean).join(' ') || (
                            <span className="text-zinc-300">—</span>
                          )}
                        </td>
                        <td className="px-5 py-2.5">
                          <Badge variant={m.status === 'active' ? 'success' : 'default'}>
                            {m.status}
                          </Badge>
                        </td>
                        <td className="px-5 py-2.5 text-zinc-500 whitespace-nowrap">
                          <span className="flex items-center gap-1">
                            <Clock size={10} className="text-zinc-300" />
                            {fmt(m.enteredAt ?? m.createdAt)}
                          </span>
                        </td>
                        {isStatic && (
                          <td className="px-5 py-2.5">
                            <button
                              onClick={() => removeMemberMutation.mutate(m.id)}
                              disabled={removeMemberMutation.isPending}
                              className="p-1 text-zinc-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                              title="Remove member"
                            >
                              <Trash2 size={12} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add member modal — static segments only */}
      {isStatic && (
        <AddSegmentMemberModal
          segmentId={id!}
          open={showAddMember}
          onClose={() => setShowAddMember(false)}
          existingMemberIds={existingMemberIds}
        />
      )}
    </div>
  );
}
